import { visuallyHidden } from "@mui/utils";
import { PROVIDER_SEARCH_PAGES, TRACK_EVENTS } from "core/consts";
import { FilterValueStartDate } from "core/types";
import { addDays, addWeeks, getUnixTime } from "date-fns";
import ConnectedRadioChipGroup, {
  RadioChipGroupProps,
} from "ds_legacy/components/RadioChipGroup";
import RSButton from "ds_legacy/components/RSButton";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import { Body, FONT_SIZE_20 } from "ds_legacy/materials/typography";
import { useProviderSearchPageData } from "dsl/molecules/ProviderSearchAppWrapper/PageMetaData";
import { Iterable } from "immutable";
import {
  SimpleFormRenderProps,
  convertModelDefinition,
  validateModel,
  valueDef,
} from "react-forms-state";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import {
  FormStepWrapper,
  FunnelFormData,
  FunnelSteps,
  STEP_SERVICES,
  STEP_START_DATE,
  getAccessibleTitleWithStep,
} from "../common";

const TODAY = getUnixTime(new Date());
const THREE_DAYS = getUnixTime(addDays(new Date(), 3));
const TWO_WEEKS = getUnixTime(addWeeks(new Date(), 2));

const getTrackingTranslation = (date: number | undefined) => {
  switch (date) {
    case TODAY:
      return "As soon as possible";
    case THREE_DAYS:
      return "In a few days";
    case TWO_WEEKS:
      return "In a few weeks";
    default:
      return undefined;
  }
};

const modelDefinition = convertModelDefinition({
  ...valueDef("start_date", {
    convertOut: (output) => {
      if (!output) return null;
      const outputValue = Iterable.isIterable(output) ? output.toJS() : output;
      return outputValue;
    },
  }),
});

export function StartDate({
  formData,
  onSubmit,
  setCurrentStep,
  setFormData,
}: {
  formData: FunnelFormData;
  onSubmit: (data: FunnelFormData) => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<FunnelSteps>>;
  setFormData: React.Dispatch<React.SetStateAction<FunnelFormData>>;
}) {
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  useProviderSearchPageData(PROVIDER_SEARCH_PAGES.ONBOARDING_FUNNEL_START_DATE);

  const options: RadioChipGroupProps["options"] = [
    {
      name: translations.providersearch.onboardingFunnel.fourthStep.asap,
      value: TODAY,
    },
    {
      name: translations.providersearch.onboardingFunnel.fourthStep.days,
      value: THREE_DAYS,
    },
    {
      name: translations.providersearch.onboardingFunnel.fourthStep.weeks,
      value: TWO_WEEKS,
    },
  ];

  return (
    <SimpleFormRenderProps
      asHtmlForm
      formInputValue={formData}
      modelDefinition={modelDefinition}
      validate={validateModel(modelDefinition)}
      onSubmit={({
        start_date,
      }: {
        start_date: FilterValueStartDate | undefined;
      }) => {
        trackEvent({
          name: TRACK_EVENTS.PROVIDER_SEARCH_FUNNEL_START_DATE_SELECTED,
          selection: getTrackingTranslation(start_date),
          start_date: start_date,
        });
        onSubmit({ start_date });
      }}
    >
      {({ submit }) => {
        return (
          <FormStepWrapper
            formData={formData}
            step={STEP_START_DATE}
            testId="start_date-select-wrapper"
            title={
              translations.providersearch.onboardingFunnel.fourthStep.question
            }
          >
            <>
              <ConnectedRadioChipGroup
                chipStyle={{
                  height: sizing(6),
                  justifyContent: "center",
                  width: "100%",
                }}
                elementName="start_date"
                formLabelSx={{
                  fontSize: FONT_SIZE_20,
                  margin: margin(0, 0, 2),
                  ...visuallyHidden,
                }}
                id="start_date"
                isSquared
                label={getAccessibleTitleWithStep({
                  step: STEP_START_DATE,
                  title:
                    translations.providersearch.onboardingFunnel.fourthStep
                      .question,
                  translations,
                })}
                options={options}
                radioGroupSx={{
                  flexDirection: "column",
                  margin: margin(1, 0),
                }}
              />
              <Body
                as="p"
                margin={margin(0)}
                style={{ padding: padding(1, 0) }}
              >
                {
                  translations.providersearch.onboardingFunnel
                    .textDontKnowStartSearch
                }
              </Body>
              <div
                style={{
                  display: "flex",
                  gap: sizing(2),
                  justifyContent: "center",
                  padding: padding(3, 0, 2),
                  width: "100%",
                }}
              >
                <RSButton
                  buttonWrapperStyle={{
                    width: "100%",
                  }}
                  color="primary"
                  id="go-back-on-startdate-step"
                  loading="na"
                  style={{ margin: margin(0), width: "100%" }}
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      start_date: undefined,
                    });
                    setCurrentStep(STEP_SERVICES);
                  }}
                >
                  {translations.providersearch.onboardingFunnel.backButton}
                </RSButton>
                <RSButton
                  buttonWrapperStyle={{
                    width: "100%",
                  }}
                  color="primary"
                  id="confirm-startdate"
                  loading="na"
                  style={{ margin: margin(0), width: "100%" }}
                  type="submit"
                  variant="contained"
                  onClick={submit}
                >
                  {
                    translations.providersearch.onboardingFunnel
                      .startSearchButton
                  }
                </RSButton>
              </div>
            </>
          </FormStepWrapper>
        );
      }}
    </SimpleFormRenderProps>
  );
}
